import { computed, inject, Injectable, signal } from '@angular/core';
import { cancelableResource } from '@softline/core';
import {
  StockLevelRow,
  toStockLevelRows,
} from '../functions/stock-levels.function';
import { ProductRepository } from '../repositories/product.repository';
import { PurchasingPriceResponse, SalesPriceResponse } from '../types/prices';
import { ProductResponse } from '../types/product';
import { ProductImageResponse } from '../types/product-image';
import { PromotionEntryResponse } from '../types/promotion';
import { ProductSalesOrganisationStatusResponse } from '../types/sales-organisation-status';
import {
  ProductStockAmountResponse,
  ProductStockStorageLocationResponse,
  StockLevelRequest,
} from '../types/stock';
import { SupplierInformationResponse } from '../types/supplier-information';
import { UnitConversionResponse } from '../types/unit-conversion';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private readonly repository = inject(ProductRepository);

  readonly productId = signal<string | null>(null);

  private readonly productResource = cancelableResource({
    params: () => this.productId(),
    // No catch: the main product load surfaces its failure on the resource so
    // consumers can show an error state; the sub-resources stay silent.
    loader: async ({ params: id }) => {
      if (!id) return null;
      return await this.repository.getById(id);
    },
  });


  private readonly imageProductId = computed(() => {
    const id = this.productId();
    if (!id) return null;
    const product = this.product();
    if (!product || String(product.id) !== String(id)) return null;
    return product.hasMainImage === false ? null : id;
  });

  private readonly mainImageResource = cancelableResource({
    params: () => this.imageProductId(),
    loader: async ({ params: id }) => {
      if (!id) return null;
      try {
        return await this.repository.getMainImage(id);
      } catch {
        return null;
      }
    },
  });

  private readonly imagesResource = cancelableResource({
    params: () => this.imageProductId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        return (await this.repository.getImages(id)) ?? [];
      } catch {
        return [];
      }
    },
  });

  private readonly supplierInformationResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        return (await this.repository.getSupplierInformation(id)) ?? [];
      } catch {
        return [];
      }
    },
  });

  private readonly mainSupplierInformationResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return null;
      try {
        return (await this.repository.getMainSupplierInformation(id)) ?? null;
      } catch {
        return null;
      }
    },
  });

  private readonly unitConversionsResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        return (await this.repository.getUnitConversions(id)) ?? [];
      } catch {
        return [];
      }
    },
  });

  private readonly salesPricesResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        return (await this.repository.getSalesPrices(id)) ?? [];
      } catch {
        return [];
      }
    },
  });

  private readonly salesOrganisationStatusesResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        return (await this.repository.getSalesOrganisationStatuses(id)) ?? [];
      } catch {
        return [];
      }
    },
  });

  /** Lagerstände je Lager incl. Dispostand — every further flag is another
      backend query, so nothing else is requested. Rows come back unordered, so
      they are sorted by Lagerkürzel here. */
  private readonly stockLevelsResource = cancelableResource({
    params: () => this.productId(),
    loader: async ({ params: id }) => {
      if (!id) return [];
      try {
        const rows =
          (await this.repository.getStockLevels(id, { disposition: true })) ?? [];
        return [...rows].sort((a, b) =>
          (a.stock?.key ?? '').localeCompare(b.stock?.key ?? '')
        );
      } catch {
        return [];
      }
    },
  });

  readonly product = computed(() => {
    if (this.productResource.error()) return null;
    return this.productResource.value() ?? null;
  });
  readonly isLoading = this.productResource.isLoading;
  readonly error = this.productResource.error;
  /** Retry the failed main product load. */
  readonly reload = (): void => {
    this.productResource.reload();
  };
  readonly mainImage = this.mainImageResource.value;
  readonly images = this.imagesResource.value;
  readonly supplierInformation = this.supplierInformationResource.value;
  readonly isLoadingSupplierInformation = this.supplierInformationResource.isLoading;
  readonly mainSupplierInformation = this.mainSupplierInformationResource.value;
  readonly unitConversions = this.unitConversionsResource.value;
  readonly isLoadingUnitConversions = this.unitConversionsResource.isLoading;
  readonly salesPrices = this.salesPricesResource.value;
  readonly isLoadingSalesPrices = this.salesPricesResource.isLoading;
  readonly stockLevels = this.stockLevelsResource.value;
  readonly isLoadingStockLevels = this.stockLevelsResource.isLoading;
  readonly salesOrganisationStatuses = this.salesOrganisationStatusesResource.value;
  readonly isLoadingSalesOrganisationStatuses =
    this.salesOrganisationStatusesResource.isLoading;

  async loadProduct(id: string): Promise<ProductResponse> {
    return this.repository.getById(id);
  }


  async loadStockLevels(
    id: string,
    request: StockLevelRequest = {}
  ): Promise<StockLevelRow[]> {
    const rows = (await this.repository.getStockLevels(id, request)) ?? [];
    return toStockLevelRows(rows);
  }

  async loadStockAmount(id: string): Promise<ProductStockAmountResponse | null> {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return null;
    const rows = await this.repository.getStockAmounts([numericId]);
    return rows?.find((r) => r.itemId === numericId) ?? rows?.[0] ?? null;
  }

  async loadUnitConversions(id: string): Promise<UnitConversionResponse[]> {
    return this.repository.getUnitConversions(id);
  }

  async loadSupplierInformation(id: string): Promise<SupplierInformationResponse[]> {
    return this.repository.getSupplierInformation(id);
  }

  async loadMainSupplierInformation(id: string): Promise<SupplierInformationResponse> {
    return this.repository.getMainSupplierInformation(id);
  }

  async loadStorageLocations(
    id: string
  ): Promise<ProductStockStorageLocationResponse[]> {
    return this.repository.getStorageLocations(id);
  }

  async loadSalesPrices(id: string): Promise<SalesPriceResponse[]> {
    return this.repository.getSalesPrices(id);
  }

  async loadSalesOrganisationStatuses(
    id: string
  ): Promise<ProductSalesOrganisationStatusResponse[]> {
    return this.repository.getSalesOrganisationStatuses(id);
  }

  async loadPurchasingPrices(id: string): Promise<PurchasingPriceResponse[]> {
    return this.repository.getPurchasingPrices(id);
  }

  async loadPromotions(id: string): Promise<PromotionEntryResponse[]> {
    return this.repository.getPromotions(id);
  }

  async loadImages(id: string): Promise<ProductImageResponse[]> {
    return this.repository.getImages(id);
  }

  async loadMainImage(id: string): Promise<ProductImageResponse> {
    return this.repository.getMainImage(id);
  }
}
